import { el, parseNum } from '../../lib/dom.js';

/** Formular-Widgets: binden Eingaben an Getter/Setter und melden Änderungen. */

interface FieldOpts {
  hint?: string;
  placeholder?: string;
  wide?: boolean;
  unit?: string;
  id?: string;
}

let fieldSeq = 0;
const nextId = () => `f-${++fieldSeq}`;

function wrapField(label: string, input: HTMLElement, opts: FieldOpts): HTMLElement {
  const f = el('div', { class: 'field' + (opts.wide ? ' wide' : '') });
  const id = opts.id ?? nextId();
  input.setAttribute('id', id);
  f.append(el('label', { for: id }, label));
  if (opts.unit) {
    const w = el('div', { class: 'unit-wrap' });
    w.append(input, el('span', { class: 'unit' }, opts.unit));
    f.append(w);
  } else {
    f.append(input);
  }
  if (opts.hint) f.append(el('div', { class: 'hint' }, opts.hint));
  return f;
}

export function fieldText(
  label: string,
  get: () => string,
  set: (v: string) => void,
  onInput: () => void,
  opts: FieldOpts = {},
): HTMLElement {
  const input = el('input', { type: 'text', placeholder: opts.placeholder ?? '' });
  input.value = get();
  input.addEventListener('input', () => {
    set(input.value);
    onInput();
  });
  return wrapField(label, input, opts);
}

export function fieldNum(
  label: string,
  get: () => number | null,
  set: (v: number | null) => void,
  onInput: () => void,
  opts: FieldOpts = {},
): HTMLElement {
  const input = el('input', {
    type: 'number',
    inputmode: 'decimal',
    min: '0',
    placeholder: opts.placeholder ?? '',
  });
  const v = get();
  if (v !== null) input.value = String(v);
  input.addEventListener('input', () => {
    set(parseNum(input.value));
    onInput();
  });
  return wrapField(label, input, opts);
}

export function fieldDate(
  label: string,
  get: () => string | null,
  set: (v: string | null) => void,
  onInput: () => void,
  opts: FieldOpts = {},
): HTMLElement {
  const input = el('input', { type: 'date' });
  input.value = get() ?? '';
  input.addEventListener('input', () => {
    set(input.value || null);
    onInput();
  });
  return wrapField(label, input, opts);
}

export function fieldSelect(
  label: string,
  options: { value: string; label: string }[],
  get: () => string,
  set: (v: string) => void,
  onChange: () => void,
  opts: FieldOpts = {},
): HTMLElement {
  const select = el('select');
  for (const o of options) {
    const opt = el('option', { value: o.value }, o.label);
    if (o.value === get()) opt.selected = true;
    select.append(opt);
  }
  select.addEventListener('change', () => {
    set(select.value);
    onChange();
  });
  return wrapField(label, select, opts);
}

export function fieldTextarea(
  label: string,
  get: () => string,
  set: (v: string) => void,
  onInput: () => void,
  opts: FieldOpts = {},
): HTMLElement {
  const input = el('textarea', { placeholder: opts.placeholder ?? '' });
  input.value = get();
  input.addEventListener('input', () => {
    set(input.value);
    onInput();
  });
  return wrapField(label, input, { ...opts, wide: opts.wide ?? true });
}

export function fieldSlider(
  label: string,
  get: () => number,
  set: (v: number) => void,
  onInput: () => void,
  opts: FieldOpts & { max?: number; step?: number } = {},
): HTMLElement {
  const f = el('div', { class: 'field' + (opts.wide ? ' wide' : '') });
  const id = nextId();
  f.append(el('label', { for: id }, label));
  const row = el('div', { class: 'sliderrow' });
  const slider = el('input', {
    id,
    type: 'range',
    min: '0',
    max: String(opts.max ?? 100),
    step: String(opts.step ?? 5),
  });
  slider.value = String(get());
  const out = el('output', {}, `${get()} %`);
  slider.addEventListener('input', () => {
    set(Number(slider.value));
    out.textContent = `${slider.value} %`;
    onInput();
  });
  row.append(slider, out);
  f.append(row);
  return f;
}
